import { useLayoutEffect, useRef, useState } from "react";
import { ApiError, messageOf } from "../api/http";
import { changeIncident, readRunRecord, type RunRecord } from "../api/runRecord";
import { useReadOnly } from "./readOnly";
import { readableTime } from "./runPresentation";

/** The action area belongs to this investigation's incident, not to its run status. */
export function RunIncident({ record, onRecord }: { record: RunRecord; onRecord: (record: RunRecord) => void }) {
  const readOnly = useReadOnly();
  const [pending, setPending] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const busy = useRef(false);
  const generation = useRef(0);
  // Set up the request lifetime before the committed button can be pressed.
  useLayoutEffect(() => {
    generation.current++;
    busy.current = false;
    setPending(false);
    setSaid(null);
    return () => { generation.current++; };
  }, [record.run_id, readOnly]);
  const incident = record.incident;
  if (!incident) return null;
  const resolved = incident.status === "closed";
  const live = incident.status === "open";
  async function change() {
    if (busy.current || !incident) return;
    busy.current = true;
    setPending(true);
    setSaid(null);
    const current = generation.current;
    try {
      const next = await changeIncident(incident);
      if (generation.current !== current) return;
      onRecord({ ...record, incident: { ...incident, ...next } });
    } catch (error) {
      if (generation.current !== current) return;
      if (error instanceof ApiError && error.status === 409) {
        setSaid("The incident changed since this page was loaded. Reading its current state…");
        try {
          const next = await readRunRecord(record.run_id);
          if (generation.current !== current) return;
          onRecord(next);
          setSaid("The incident changed since this page was loaded. Its current state is shown.");
        } catch (reason) {
          if (generation.current !== current) return;
          setSaid(`The incident changed since this page was loaded. Its current state could not be read. ${messageOf(reason)} Try again.`);
        }
      } else {
        setSaid(error instanceof ApiError && error.status === 403
          ? "Your role does not allow you to change this incident."
          : `${messageOf(error)} Try again.`);
      }
    } finally {
      if (generation.current === current) {
        busy.current = false;
        setPending(false);
      }
    }
  }
  return (
    <div className="investigation-incident">
      <p role="status" aria-label="Incident status" aria-live="polite" aria-atomic="true">
        Incident: {resolved ? "Resolved" : "Unresolved"}
        {resolved && incident.resolved_at ? ` · ${readableTime(incident.resolved_at)}` : null}
      </p>
      {live ? (
        <p className="muted">The incident can be resolved once the investigation has finished.</p>
      ) : incident.may_change === true && !readOnly ? (
        <button type="button" className="btn btn--quiet" aria-disabled={pending} onClick={() => void change()}>
          {resolved ? "Reopen" : "Resolve"}
        </button>
      ) : null}
      {said ? <p role="status" className="muted">{said}</p> : null}
    </div>
  );
}
